import { useQuery } from '@tanstack/react-query';
import { AnalyticalPanel, DataQualityState, StatusBadge, Table } from '@atlas/ui';
import { api } from '../../lib/api';
import { ExplainActions, PageHeader } from '../../components/shared';
import { MetricExplainer } from '../../components/explain';
import { PageSkeleton } from '../../components/PageSkeleton';

export function PortfolioPage() {
  const { data, isLoading, isError } = useQuery({ queryKey: ['portfolio'], queryFn: api.portfolio });

  if (isLoading) return <PageSkeleton blocks={4} />;
  if (isError || !data) return <DataQualityState state="unavailable" title="Portfolio unavailable" />;

  const bufferMonths = data.liquidityFloor?.currentMonths ?? 0;
  const bufferTarget = data.liquidityFloor?.requiredMonths ?? 6;
  const bufferStatus = data.liquidityFloor?.status ?? 'unknown';

  const flatExposures = (data.exposures ?? []).flatMap((e) =>
    (e.buckets ?? []).map((b) => ({
      dimension: e.dimension,
      key: b.label,
      weightPct: b.weightPct,
    })),
  );

  return (
    <div className="space-y-6">
      <PageHeader title="Portfolio" subtitle="Household constraints before security selection." />
      <ExplainActions screenName="Portfolio" />

      <section className="rounded-[var(--radius-canvas)] border border-[var(--color-border-strong)] bg-[var(--color-surface-1)] p-6">
        <p className="text-xs uppercase tracking-widest text-[var(--color-atlas-gold)]">Household buffer first</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <MetricExplainer
            label="Emergency buffer"
            value={String(bufferMonths)}
            unit="months"
            glossaryTermId="glossary.survival-buffer"
            status={bufferStatus === 'healthy' ? 'fresh' : 'partial'}
            explainBody={data.survivalBuffer?.rationale}
          />
          <MetricExplainer
            label="Policy floor"
            value={String(bufferTarget)}
            unit="months"
            glossaryTermId="glossary.survival-buffer"
          />
          <div>
            <p className="text-xs text-[var(--color-text-muted)]">Liquidity status</p>
            <StatusBadge status={bufferStatus === 'healthy' ? 'fresh' : 'warning'}>
              {bufferStatus}
            </StatusBadge>
          </div>
        </div>
      </section>

      <AnalyticalPanel title="Exposure matrix">
        <Table
          caption="Portfolio exposures"
          rows={flatExposures}
          getRowKey={(r) => `${r.dimension}-${r.key}`}
          columns={[
            { key: 'dim', header: 'Dimension', cell: (r) => r.dimension },
            { key: 'key', header: 'Key', cell: (r) => r.key },
            { key: 'w', header: 'Weight %', cell: (r) => r.weightPct, align: 'right' },
          ]}
        />
      </AnalyticalPanel>

      <AnalyticalPanel title="Positions">
        <Table
          caption="Holdings"
          rows={data.positions ?? []}
          getRowKey={(r) => r.positionId}
          columns={[
            { key: 'sym', header: 'Symbol', cell: (r) => r.symbol },
            { key: 'role', header: 'Role', cell: (r) => r.role },
            { key: 'w', header: 'Weight %', cell: (r) => r.weightPct, align: 'right' },
          ]}
        />
      </AnalyticalPanel>
    </div>
  );
}
